import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/objectWithoutPropertiesLoose';
import React from 'react';
import PropTypes from 'prop-types';
import { a as addClassNames, c as classNameProps } from './classNames-6a4763fd.js';
import { a as addSpacingClasses, m as marginProps, p as paddingProps } from './spacing-d13ff368.js';
import { p as props, d as defaults } from './componentEl-33ff2aeb.js';

function GroupItem(_ref) {
  let {
    children,
    componentEl: Component,
    flex,
    margin,
    className,
    fullWidth
  } = _ref,
      otherProps = _objectWithoutPropertiesLoose(_ref, ["children", "componentEl", "flex", "margin", "padding", "className", "fullWidth"]);

  let cssClasses = ['st_grp__item'];
  if (fullWidth) cssClasses.push('st_grp__item--fullwidth');
  if (flex) cssClasses.push('st_grp__item--flex');
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    margin,
    cssClasses
  });
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), children);
}

GroupItem.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,

  /** extra CSS classes, if any */
  className: classNameProps,

  /** margin for component, if any */
  margin: marginProps,

  /** padding for component, if any */
  padding: paddingProps,

  /** whether item should take up 100% of available width */
  fullWidth: PropTypes.bool,

  /** item takes up rest of available space */
  flex: PropTypes.bool
};
GroupItem.defaultProps = {
  componentEl: defaults,
  className: null,
  margin: null,
  padding: null,
  fullWidth: false,
  flex: false
};
GroupItem.displayName = 'Group.Item';

function Group(_ref) {
  let {
    children,
    componentEl: Component,
    align,
    valign,
    direction,
    margin,
    inline,
    className,
    gap,
    colgap,
    rowgap,
    wrap
  } = _ref,
      otherProps = _objectWithoutPropertiesLoose(_ref, ["children", "componentEl", "align", "valign", "direction", "margin", "inline", "className", "gap", "colgap", "rowgap", "wrap"]);

  let cssClasses = ['st_grp', "st_grp--" + direction, "st_grp--a-" + align, "st_grp--v-" + valign];
  if (gap && !rowgap && !colgap) cssClasses.push("st_grp--g-" + gap);
  if (rowgap) cssClasses.push("st_grp--gr-" + rowgap);
  if (colgap) cssClasses.push("st_grp--gc-" + colgap);
  if (inline) cssClasses.push('st_grp--inline');
  if (wrap) cssClasses.push('st_grp--wrap');
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    margin,
    cssClasses
  });
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), children);
}

Group.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,

  /** extra CSS classes, if any */
  className: classNameProps,

  /** whether to display flex inline */
  inline: PropTypes.bool,

  /** margin for component, if any */
  margin: marginProps,

  /** direction of items. Row = horizontal, Column = vertical */
  direction: PropTypes.oneOf(['row', 'column']),

  /** how to align items horizontally */
  align: PropTypes.oneOf(['center', 'start', 'end', 'stretch', 'space-between']),

  /** space between items, if any */
  gap: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl', 'none']),

  /** space between columns, if not set via `gap` */
  colgap: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl', 'none']),

  /** space between rows, if not set via `gap` */
  rowgap: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl', 'none']),

  /** how to align items vertically */
  valign: PropTypes.oneOf(['baseline', 'center', 'start', 'end']),

  /** only used in row mode. Whether to wrap if not enough horizontal space */
  wrap: PropTypes.bool
};
Group.defaultProps = {
  componentEl: defaults,
  inline: false,
  className: null,
  margin: null,
  direction: 'row',
  align: 'start',
  valign: 'center',
  gap: 'mlg',
  wrap: true,
  colgap: null,
  rowgap: null
};
Group.Item = GroupItem;

export default Group;
